import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const GeolocationContext = createContext();

export const useGeolocation = () => {
  const context = useContext(GeolocationContext);
  if (!context) {
    throw new Error('useGeolocation must be used within a GeolocationProvider');
  }
  return context;
};

// Country to language mapping
const COUNTRY_LANGUAGE_MAP = {
  // English
  'US': 'en', 'GB': 'en', 'AU': 'en', 'CA': 'en', 'NZ': 'en', 'IE': 'en',
  // French
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'LU': 'fr', 'MC': 'fr',
  // Spanish
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es', 'PE': 'es', 'VE': 'es',
  // Portuguese
  'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt',
  // German
  'DE': 'de', 'AT': 'de', 'LI': 'de',
};

// Crisis hotlines by country
export const CRISIS_HOTLINES = {
  'US': {
    country: 'United States',
    hotlines: [
      { name: 'National Suicide Prevention Lifeline', phone: '988', description: '24/7 crisis support' },
      { name: 'Crisis Text Line', phone: 'Text HOME to 741741', description: 'Text-based support' },
      { name: 'SAMHSA National Helpline', phone: '1-800-662-4357', description: 'Mental health referrals' },
    ]
  },
  'CA': {
    country: 'Canada',
    hotlines: [
      { name: 'Crisis Services Canada', phone: '1-833-456-4566', description: '24/7 crisis support' },
      { name: 'Kids Help Phone', phone: '1-800-668-6868', description: 'Youth support' },
      { name: 'Crisis Text Line', phone: 'Text CONNECT to 686868', description: 'Text-based support' },
    ]
  },
  'GB': {
    country: 'United Kingdom',
    hotlines: [
      { name: 'Samaritans', phone: '116 123', description: '24/7 emotional support' },
      { name: 'SHOUT', phone: 'Text SHOUT to 85258', description: 'Text-based support' },
      { name: 'CALM', phone: '0800 58 58 58', description: 'For men in crisis' },
    ]
  },
  'FR': {
    country: 'France',
    hotlines: [
      { name: 'SOS Amitié', phone: '09 72 39 40 50', description: 'Soutien émotionnel 24h/24' },
      { name: 'Fil Santé Jeunes', phone: '0 800 235 236', description: 'Pour les jeunes' },
      { name: 'SOS Suicide Phénix', phone: '01 45 39 40 00', description: 'Prévention du suicide' },
    ]
  },
  'DE': {
    country: 'Germany',
    hotlines: [
      { name: 'Telefonseelsorge', phone: '0800 111 0 111', description: '24/7 Krisenunterstützung' },
      { name: 'Telefonseelsorge (Alt)', phone: '0800 111 0 222', description: 'Kostenlose Beratung' },
      { name: 'Nummer gegen Kummer', phone: '116 111', description: 'Für Kinder und Jugendliche' },
    ]
  },
  'ES': {
    country: 'Spain',
    hotlines: [
      { name: 'Teléfono de la Esperanza', phone: '717 003 717', description: 'Apoyo emocional 24h' },
      { name: 'ANAR', phone: '900 20 20 10', description: 'Ayuda a niños y adolescentes' },
      { name: 'Línea de Atención a la Conducta Suicida', phone: '024', description: 'Prevención del suicidio' },
    ]
  },
  'PT': {
    country: 'Portugal',
    hotlines: [
      { name: 'SOS Voz Amiga', phone: '213 544 545', description: 'Apoio emocional' },
      { name: 'Telefone da Amizade', phone: '222 080 707', description: 'Linha de apoio' },
      { name: 'SNS 24', phone: '808 24 24 24', description: 'Saúde mental' },
    ]
  },
  'BR': {
    country: 'Brazil',
    hotlines: [
      { name: 'CVV - Centro de Valorização da Vida', phone: '188', description: 'Apoio emocional 24h' },
      { name: 'CAPS', phone: 'Consulte seu município', description: 'Centro de Atenção Psicossocial' },
    ]
  },
  'MX': {
    country: 'Mexico',
    hotlines: [
      { name: 'SAPTEL', phone: '55 5259-8121', description: 'Apoyo emocional 24h' },
      { name: 'Línea de la Vida', phone: '800 911 2000', description: 'Prevención del suicidio' },
    ]
  },
  'AU': {
    country: 'Australia',
    hotlines: [
      { name: 'Lifeline', phone: '13 11 14', description: '24/7 crisis support' },
      { name: 'Beyond Blue', phone: '1300 22 4636', description: 'Anxiety and depression' },
      { name: 'Kids Helpline', phone: '1800 55 1800', description: 'For young people' },
    ]
  },
  'IN': {
    country: 'India',
    hotlines: [
      { name: 'iCall', phone: '9152987821', description: 'Mental health support' },
      { name: 'Vandrevala Foundation', phone: '1860-2662-345', description: '24/7 helpline' },
      { name: 'AASRA', phone: '91-22-27546669', description: 'Crisis intervention' },
    ]
  },
  'DEFAULT': {
    country: 'International',
    hotlines: [
      { name: 'International Association for Suicide Prevention', phone: 'https://www.iasp.info/resources/Crisis_Centres/', description: 'Find local resources' },
      { name: 'Befrienders Worldwide', phone: 'https://www.befrienders.org/', description: 'Global crisis support network' },
    ]
  }
};

export const GeolocationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [location, setLocation] = useState(() => {
    const saved = localStorage.getItem('calvio_location');
    return saved ? JSON.parse(saved) : null;
  });
  const [consentStatus, setConsentStatus] = useState(() => {
    return localStorage.getItem('calvio_location_consent') || 'pending'; // 'pending', 'granted', 'denied'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConsentDialog, setShowConsentDialog] = useState(false);

  // Check if we should show consent dialog - only for authenticated users after auth loading completes
  useEffect(() => {
    // Don't show dialog while auth is still loading
    if (authLoading) {
      return;
    }
    
    if (user && consentStatus === 'pending' && !location) {
      // Delay showing dialog to not overwhelm user on first load
      const timer = setTimeout(() => {
        setShowConsentDialog(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (!user) {
      // Ensure dialog is hidden when user is not authenticated
      setShowConsentDialog(false);
    }
  }, [user, authLoading, consentStatus, location]);

  // Reverse geocode coordinates to get location details
  const reverseGeocode = async (latitude, longitude) => {
    try {
      // Using free OpenStreetMap Nominatim API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'Calvio Mental Wellness App'
          }
        }
      );
      
      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      const address = data.address || {};
      
      return {
        country: address.country || 'Unknown',
        countryCode: address.country_code?.toUpperCase() || 'XX',
        city: address.city || address.town || address.village || address.municipality || 'Unknown',
        region: address.state || address.province || address.region || '',
        latitude,
        longitude
      };
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      // Fallback: try IP-based geolocation
      return await getLocationByIP();
    }
  };

  // Fallback: Get location by IP (less precise but doesn't require permission)
  const getLocationByIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('IP geolocation failed');
      
      const data = await response.json();
      return {
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'XX',
        city: data.city || 'Unknown',
        region: data.region || '',
        latitude: data.latitude,
        longitude: data.longitude
      };
    } catch (err) {
      console.error('IP geolocation error:', err);
      return null;
    }
  };

  // Request browser geolocation permission
  const requestLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation not supported');
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // Cache for 5 minutes
        });
      });

      const { latitude, longitude } = position.coords;
      const locationData = await reverseGeocode(latitude, longitude);

      if (locationData) {
        setLocation(locationData);
        localStorage.setItem('calvio_location', JSON.stringify(locationData));
        localStorage.setItem('calvio_location_consent', 'granted');
        setConsentStatus('granted');

        // Auto-set language based on country
        const detectedLang = COUNTRY_LANGUAGE_MAP[locationData.countryCode];
        if (detectedLang && detectedLang !== i18n.language) {
          i18n.changeLanguage(detectedLang);
          localStorage.setItem('i18nextLng', detectedLang);
        }
      }

      setShowConsentDialog(false);
    } catch (err) {
      console.error('Geolocation error:', err);
      
      // If browser permission denied, try IP-based fallback
      if (err.code === 1) { // PERMISSION_DENIED
        const ipLocation = await getLocationByIP();
        if (ipLocation) {
          setLocation(ipLocation);
          localStorage.setItem('calvio_location', JSON.stringify(ipLocation));
          
          // Auto-set language
          const detectedLang = COUNTRY_LANGUAGE_MAP[ipLocation.countryCode];
          if (detectedLang && detectedLang !== i18n.language) {
            i18n.changeLanguage(detectedLang);
            localStorage.setItem('i18nextLng', detectedLang);
          }
        }
      }
      
      setError(err.message);
      localStorage.setItem('calvio_location_consent', 'denied');
      setConsentStatus('denied');
      setShowConsentDialog(false);
    } finally {
      setLoading(false);
    }
  }, [i18n]);

  // Deny location access
  const denyLocation = useCallback(() => {
    localStorage.setItem('calvio_location_consent', 'denied');
    setConsentStatus('denied');
    setShowConsentDialog(false);
  }, []);

  // Reset consent (allow user to change their mind)
  const resetConsent = useCallback(() => {
    localStorage.removeItem('calvio_location_consent');
    localStorage.removeItem('calvio_location');
    setConsentStatus('pending');
    setLocation(null);
    setShowConsentDialog(true);
  }, []);

  // Get crisis hotlines for current location
  const getCrisisHotlines = useCallback(() => {
    if (location?.countryCode && CRISIS_HOTLINES[location.countryCode]) {
      return CRISIS_HOTLINES[location.countryCode];
    }
    return CRISIS_HOTLINES['DEFAULT'];
  }, [location]);

  const value = {
    location,
    consentStatus,
    loading,
    error,
    showConsentDialog,
    setShowConsentDialog,
    requestLocation,
    denyLocation,
    resetConsent,
    getCrisisHotlines,
    CRISIS_HOTLINES
  };

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
};

export default GeolocationContext;
